import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box, CircularProgress, InputAdornment, Paper, Tab, Table, TableBody, TableCell,
  TableContainer, TableHead, TablePagination, TableRow, Tabs, TextField
} from '@mui/material';
import dayjs from 'dayjs';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PageHeader from '../../../components/PageHeader/PageHeader';
import CustomTabPanel from '../../../components/Tab/CustomTabPanel';
import ProductionDetailModal from '../../../components/BatchTable/ProductionDetailModal';
import LotDetailModal from './LotDetailModal';
import SearchIcon from '@mui/icons-material/Search';
import { getPlantLots } from '../../../services/ProductionService';
import { permissionOptions } from '../../../utils/Enums';

export default function PlantLots() {
  const { t } = useTranslation();
  const { locationId } = useParams();
  const [lots, setLots] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [selectedLotId, setSelectedLotId] = useState(null);
  const [productionId, setProductionId] = useState(null);
  const requestId = useRef(0);

  const userPermissions = JSON.parse(localStorage.getItem('permissions')) || [];
  const canEdit = userPermissions.includes(permissionOptions.find(p => p.label === 'MANAGE_PRODUCTION')?.id);

  const fetchLots = useCallback(async () => {
    const current = ++requestId.current;
    setLoading(true);
    try {
      const data = await getPlantLots(locationId, { search, page: page + 1, pageSize: rowsPerPage });
      if (current === requestId.current) {
        setLots(data?.lots || []);
        setTotal(data?.total || 0);
      }
    } catch (error) {
      console.error('Error fetching lots:', error);
      if (current === requestId.current) {
        setLots([]);
        setTotal(0);
      }
    } finally {
      if (current === requestId.current) setLoading(false);
    }
  }, [locationId, search, page, rowsPerPage]);

  // Debounce: la búsqueda tipea rápido; cambios de filtro o página disparan el mismo efecto.
  useEffect(() => {
    const timeout = setTimeout(fetchLots, 300);
    return () => clearTimeout(timeout);
  }, [fetchLots]);

  const handleSearch = (event) => {
    setSearch(event.target.value);
    setPage(0);
  };

  return (
    <div>
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={0}>
          <Tab label={t('plant_lots')} />
        </Tabs>
      </Box>

      <CustomTabPanel value={0} index={0}>
        <PageHeader title={t('plant_lots')} subtitle={t('plant_lots_subtitle')} />

        <TextField
          autoFocus
          fullWidth
          size="small"
          placeholder={t('lot_search_hint')}
          value={search}
          onChange={handleSearch}
          sx={{ mb: 2, maxWidth: 520 }}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
        />

        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('lot_label')}</TableCell>
                <TableCell>{t('lot_product')}</TableCell>
                <TableCell>{t('lot_entry')}</TableCell>
                <TableCell>{t('expiration')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && (
                <TableRow><TableCell colSpan={4} align="center"><CircularProgress size={24} /></TableCell></TableRow>
              )}
              {!loading && lots.length === 0 && (
                <TableRow><TableCell colSpan={4} align="center">{t('lot_empty')}</TableCell></TableRow>
              )}
              {!loading && lots.map((lot) => (
                <TableRow key={lot.id} hover sx={{ cursor: 'pointer' }} onClick={() => setSelectedLotId(lot.id)}>
                  <TableCell>{lot.lot}</TableCell>
                  <TableCell>{lot.name}</TableCell>
                  <TableCell>{dayjs(lot.entryDate).format('DD/MM/YYYY')}</TableCell>
                  <TableCell>{lot.expirationDate ? dayjs(lot.expirationDate).format('DD/MM/YYYY') : '—'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
            rowsPerPageOptions={[10, 25, 50, 100]}
            labelRowsPerPage={t('lot_rows_per_page')}
          />
        </TableContainer>

        <LotDetailModal
          lotId={selectedLotId}
          canEdit={canEdit}
          onClose={() => setSelectedLotId(null)}
          onSelectLot={setSelectedLotId}
          onOpenProduction={setProductionId}
          onUpdated={fetchLots}
        />
        <ProductionDetailModal
          locationId={locationId}
          open={!!productionId}
          productionId={productionId}
          onClose={() => setProductionId(null)}
        />
      </CustomTabPanel>
    </div>
  );
}
